<template>
    <CompassModal
        v-model:visible.sync="visible"
        size="small"
        :title="$t.__('purchases.PurchaseInvoiceModal.title')"
        class="purchase-invoice-modal"
        :class="{ 'purchase-invoice-modal--empty-footer': showErrorContent }"
        :primaryAction="primaryAction"
        :closeOnClickOverlay="false"
        @closed="closeDialog"
    >
        <template #default>
            <div class="purchase-invoice-modal__content">
                <CompassAlert
                    v-if="!(isUploading || hasFinishedUploading) || showErrorContent"
                    :type="showErrorContent ? 'error' : 'neutral'"
                    :showClose="false"
                    class="mb-0"
                    size="small"
                    :description="alertDescription"
                />
                <div
                    v-if="!(isUploading || hasFinishedUploading)"
                    class="purchase-invoice-modal__separator typographies-paragraph-medium-semibold"
                >
                    <span>{{ $t.__('common.labels.or') }}</span>
                </div>
                <PurchaseInvoiceUpload
                    v-if="!showErrorContent"
                    ref="upload"
                    :files="files"
                    :isUploading="isUploading"
                    :currentUploadPosition="currentUploadPosition"
                    :uploadQueueSize="uploadQueueSize"
                    @files-added="handleFilesAdded"
                />
                <div v-if="files.length" class="purchase-invoice-modal__list">
                    <PurchaseInvoiceFileItem
                        v-for="file in files"
                        :key="file.id"
                        :file="file"
                        :error="file.errorMessage"
                        :showRemove="canRemoveFiles"
                        :disabled="isUploading"
                        @remove="handleRemoveFile"
                    />
                </div>
            </div>
        </template>
    </CompassModal>
</template>

<script>
import axios from 'axios';
import Feature from 'Services/Feature';
import PurchaseInvoiceUpload from './PurchaseInvoiceUpload.vue';
import PurchaseInvoiceFileItem from './PurchaseInvoiceFileItem.vue';

export default {
    name: 'PurchaseInvoiceImportModal',
    components: {
        PurchaseInvoiceUpload,
        PurchaseInvoiceFileItem,
    },
    data() {
        return {
            visible: true,
            isUploading: false,
            hasFinishedUploading: false,
            files: [],
            uploadQueueSize: 0,
            processedUploads: 0,
        };
    },
    computed: {
        primaryAction() {
            return {
                label: !this.showErrorContent ? this.$t.__('common.actions.validate') : null,
                action: this.handleValidate,
                disabled: !this.canValidate,
                dataBot: 'purchases-purinvoices-list_import-validate',
            };
        },
        actionUploadUrl() {
            return this.$router.resolve({
                name: 'legacy.ocr.create',
                params: { linkedtype: 'purInvoice' },
            }).href;
        },
        technicalIssue() {
            return !Feature.isEnabled('ocr-purInvoice');
        },
        canValidate() {
            const hasUploadableFiles = this.files.some(
                (file) => file.status !== 'success' && file.status !== 'error',
            );
            return hasUploadableFiles && !this.isUploading && !this.technicalIssue;
        },
        canRemoveFiles() {
            return !this.isUploading && !this.hasFinishedUploading;
        },
        currentUploadPosition() {
            if (!this.uploadQueueSize) {
                return 0;
            }
            const nextFile = this.processedUploads + 1;
            return Math.min(nextFile, this.uploadQueueSize);
        },
        hasError() {
            return this.files.some((file) => file.status === 'error');
        },
        errorFilesCount() {
            return this.files.filter((file) => file.status === 'error').length;
        },
        showErrorContent() {
            return this.hasFinishedUploading && this.hasError;
        },
        corpId() {
            return this.$store.state.corp.id;
        },
        alertDescription() {
            if (this.showErrorContent) {
                return this.$t.n__(
                    'purchases.PurchaseInvoiceModal.error',
                    null,
                    this.errorFilesCount,
                    { count: this.errorFilesCount },
                );
            }

            return this.$t.__('purchases.PurchaseInvoiceModal.description', {
                corpId: this.corpId,
            });
        },
    },
    methods: {
        handleFilesAdded(newFiles) {
            if (!Array.isArray(newFiles) || !newFiles.length) {
                return;
            }
            this.files = [...this.files, ...newFiles];
        },
        handleRemoveFile(id) {
            if (this.isUploading || this.hasFinishedUploading) {
                return;
            }
            this.files = this.files.filter((file) => file.id !== id);
        },
        async handleValidate() {
            if (!this.canValidate) {
                return;
            }

            const filesToUpload = this.files.filter(
                (file) => file.status !== 'success' && file.status !== 'error',
            );
            if (!filesToUpload.length) {
                return;
            }

            this.isUploading = true;
            this.uploadQueueSize = filesToUpload.length;
            this.processedUploads = 0;
            let successfulUploads = 0;

            for (const file of filesToUpload) {
                file.status = 'uploading';
                file.errorMessage = '';

                try {
                    await this.sendFile(file);
                    file.status = 'success';
                    successfulUploads += 1;
                    this.processedUploads += 1;
                    this.removeUploadedFile(file.id);
                } catch (error) {
                    file.status = 'error';
                    file.errorMessage = this.$l10n.__(
                        'Fichier invalide. Vérifiez le poids et le format',
                    );
                    this.processedUploads += 1;
                }
            }

            this.isUploading = false;
            this.hasFinishedUploading = true;

            if (successfulUploads === this.uploadQueueSize && !this.hasError) {
                this.$pushToast({
                    type: 'success',
                    text: this.$t.__('purchases.PurchaseInvoiceModal.success'),
                    icon: true,
                });
                this.closeDialog(true);
                this.resetFiles();
            }
        },
        sendFile(file) {
            const formData = new FormData();
            formData.append('file', file.file);
            return axios.post(this.actionUploadUrl, formData, {
                headers: { 'Content-Type': 'multipart/form-data' },
            });
        },
        removeUploadedFile(id) {
            this.files = this.files.filter((file) => file.id !== id);
        },
        resetFiles() {
            this.files = [];
            this.uploadQueueSize = 0;
            this.processedUploads = 0;
        },
        closeDialog(redirect) {
            const ocrListRoute = '/purchase/incoming';
            this.$emit('close');
            if (redirect) {
                if (window.location.href.endsWith(ocrListRoute)) {
                    EventBus.emit('listing:refresh');
                } else {
                    window.location.href = ocrListRoute;
                }
            }
        },
    },
};
</script>

<style lang="scss" scoped>
.purchase-invoice-modal {
    z-index: 2007 !important; // The z-index for loading the list is 2006

    &__header {
        display: flex;
        align-items: center;
        justify-content: space-between;
    }

    &__content {
        display: flex;
        flex-direction: column;
        gap: var(--spacing-3);
    }

    &__separator {
        position: relative;
        text-align: center;

        &::before {
            content: '';
            position: absolute;
            top: 50%;
            left: 0;
            right: 0;
            height: 1px;
            background-color: var(--colors-common-border-lighter);
            transform: translateY(-50%);
            z-index: 0;
        }

        span {
            text-transform: uppercase;
            position: relative;
            padding: 0 var(--spacing-3);
            color: var(--colors-common-text-standard-caption);
            background-color: var(--colors-common-background-default);
            z-index: 1;
        }
    }

    &__list {
        display: flex;
        flex-direction: column;
        gap: var(--spacing-2);
        padding-bottom: var(--spacing-2);
    }

    &--empty-footer {
        :deep(.compass-modal__modal__footer) {
            min-height: 32px;
        }
    }
}
</style>
